import { z } from "zod";
import { EventParticipantIdPublicSchema } from "./branded-id.schemas.js";
import { EventParticipantStatusSchema } from "./participant-state.schemas.js";

export const FunnelStepSchema = z.object({
  step: z
    .enum([
      "participants",
      "startedWithPorter",
      "joined",
      "sentReceived",
      "connected",
    ])
    .describe("Stable, language-neutral identifier for this funnel step"),
  value: z
    .number()
    .int()
    .min(0)
    .describe("Absolute count of participants at this step"),
  percentOfTop: z
    .number()
    .describe("Percentage of this step's value relative to the first step"),
  percentOfPrevious: z
    .number()
    .describe("Percentage of this step's value relative to the previous step"),
});

export type FunnelStep = z.infer<typeof FunnelStepSchema>;

export const VipFunnelSchema = z.object({
  vip: z
    .array(FunnelStepSchema)
    .describe("Funnel steps over participants marked VIP"),
  others: z
    .array(FunnelStepSchema)
    .describe("Funnel steps over participants not marked VIP"),
});

export type VipFunnel = z.infer<typeof VipFunnelSchema>;

export const OutcomeSegmentSchema = z.object({
  key: z
    .enum(["accepted", "seen", "declined", "pending"])
    .describe("Stable, language-neutral identifier for this outcome segment"),
  count: z
    .number()
    .int()
    .min(0)
    .describe("Absolute count of delivered requests in this segment"),
  percentOfTotal: z
    .number()
    .describe("Percentage of this segment's count relative to delivered total"),
});

export type OutcomeSegment = z.infer<typeof OutcomeSegmentSchema>;

export const RequestedBeforeJoiningSchema = z.object({
  people: z
    .number()
    .int()
    .min(0)
    .describe("Distinct recipients requested before they joined"),
  requests: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests created before the recipient's first JOINED"),
  accepted: z
    .number()
    .int()
    .min(0)
    .describe(
      "Requests created before the recipient joined that are now accepted",
    ),
});

export type RequestedBeforeJoining = z.infer<
  typeof RequestedBeforeJoiningSchema
>;

export const RequestOutcomesSchema = z.object({
  total: z
    .number()
    .int()
    .min(0)
    .describe(
      "Non-cancelled requests whose recipient is currently JOINED (the 100% baseline)",
    ),
  allRequests: z
    .number()
    .int()
    .min(0)
    .describe("Non-cancelled requests, delivered or not"),
  waitingToJoin: z
    .number()
    .int()
    .min(0)
    .describe("Non-cancelled requests whose recipient has not joined yet"),
  requestedBeforeJoining: RequestedBeforeJoiningSchema.describe(
    "Delivered requests that were waiting for their recipient to join",
  ),
  segments: z
    .array(OutcomeSegmentSchema)
    .describe(
      "Outcome breakdown of delivered requests: Accepted, Seen, Declined, Pending",
    ),
});

export type RequestOutcomes = z.infer<typeof RequestOutcomesSchema>;

export const ParticipantTimeSeriesPointSchema = z.object({
  date: z.string().describe("Calendar day in the event timezone as YYYY-MM-DD"),
  joined: z
    .number()
    .int()
    .min(0)
    .describe("Participants in JOINED state as of end of this day"),
  contacted: z
    .number()
    .int()
    .min(0)
    .describe("Participants in CONTACTED state as of end of day"),
  pending: z
    .number()
    .int()
    .min(0)
    .describe(
      "Participants not joined yet (PENDING or INVITED) as of end of this day",
    ),
});

export type ParticipantTimeSeriesPoint = z.infer<
  typeof ParticipantTimeSeriesPointSchema
>;

export const ConnectionTimeSeriesPointSchema = z.object({
  date: z.string().describe("Calendar day in the event timezone as YYYY-MM-DD"),
  accepted: z
    .number()
    .int()
    .min(0)
    .describe("Requests created this day with status accepted"),
  seen: z
    .number()
    .int()
    .min(0)
    .describe("Requests created this day with status seen"),
  declined: z
    .number()
    .int()
    .min(0)
    .describe("Requests created this day with status declined"),
  pending: z
    .number()
    .int()
    .min(0)
    .describe("Requests created this day with status pending or sent"),
});

export type ConnectionTimeSeriesPoint = z.infer<
  typeof ConnectionTimeSeriesPointSchema
>;

export const JoinCurveDaySchema = z.object({
  date: z.string().describe("Calendar day in the event timezone as YYYY-MM-DD"),
  joins: z
    .number()
    .int()
    .min(0)
    .describe("Joined participants whose first JOINED falls on this day"),
  cumulative: z
    .number()
    .int()
    .min(0)
    .describe(
      "Joined participants whose first JOINED falls on or before this day",
    ),
});

export type JoinCurveDay = z.infer<typeof JoinCurveDaySchema>;

export const JoinCurveSchema = z.object({
  days: z
    .array(JoinCurveDaySchema)
    .describe(
      "Per-event-local-day first joins of currently-JOINED participants, gap-filled across the range",
    ),
  before: z
    .number()
    .int()
    .min(0)
    .describe("Joined participants who first joined before the event start"),
  during: z
    .number()
    .int()
    .min(0)
    .describe(
      "Joined participants who first joined between event start and end, inclusive",
    ),
  after: z
    .number()
    .int()
    .min(0)
    .describe("Joined participants who first joined after the event end"),
  medianImportToJoinMs: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Nearest-rank median of participant creation to first JOINED, in ms (null when no JOINED participant has a JOINED entry)",
    ),
});

export type JoinCurve = z.infer<typeof JoinCurveSchema>;

export const RequestPhaseCountsSchema = z.object({
  requests: z
    .number()
    .int()
    .min(0)
    .describe("Non-cancelled requests created in this phase"),
  accepts: z
    .number()
    .int()
    .min(0)
    .describe(
      "First accepts that happened in this phase, whenever the request was created; not a subset of this phase's requests",
    ),
});

export type RequestPhaseCounts = z.infer<typeof RequestPhaseCountsSchema>;

export const RequestPhasesSchema = z.object({
  before: RequestPhaseCountsSchema.describe("Before the event start"),
  during: RequestPhaseCountsSchema.describe(
    "Between event start and end, inclusive",
  ),
  after: RequestPhaseCountsSchema.describe("After the event end"),
});

export type RequestPhases = z.infer<typeof RequestPhasesSchema>;

export const OpenedToAcceptedSchema = z.object({
  accepted: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests whose current status is accepted"),
  medianMs: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Nearest-rank median of first seen to first accepted over delivered requests with both entries, in ms (null when no delivered request has both)",
    ),
});

export type OpenedToAccepted = z.infer<typeof OpenedToAcceptedSchema>;

export const ResponseTimeSchema = z.object({
  delivered: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests, the base for the within-window counts"),
  opened: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests with a seen entry"),
  medianMs: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Nearest-rank median of availability to first seen over opened requests, in ms (null when none opened)",
    ),
  openedWithin1h: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests first seen at most 1 h after availability"),
  openedWithin24h: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests first seen at most 24 h after availability"),
  openedToAccepted: OpenedToAcceptedSchema.describe(
    "Delivered requests accepted, with the median time from first seen to first accepted",
  ),
});

export type ResponseTime = z.infer<typeof ResponseTimeSchema>;

export const NoteAcceptedCountsSchema = z.object({
  accepted: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests in this group whose status is accepted"),
  delivered: z
    .number()
    .int()
    .min(0)
    .describe("Delivered requests in this group"),
});

export type NoteAcceptedCounts = z.infer<typeof NoteAcceptedCountsSchema>;

export const RequestNotesSchema = z.object({
  withNote: z
    .number()
    .int()
    .min(0)
    .describe("Non-cancelled requests that carry a note"),
  allRequests: z
    .number()
    .int()
    .min(0)
    .describe("Non-cancelled requests, delivered or not"),
  deliveredWithNote: NoteAcceptedCountsSchema.describe(
    "Delivered requests with a note, and how many were accepted",
  ),
  deliveredWithoutNote: NoteAcceptedCountsSchema.describe(
    "Delivered requests without a note, and how many were accepted",
  ),
});

export type RequestNotes = z.infer<typeof RequestNotesSchema>;

export const ActivityDistributionSchema = z.object({
  activePeople: z
    .number()
    .int()
    .min(0)
    .describe("Joined participants with a value of at least 1"),
  median: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Nearest-rank median over joined participants, zeros included (null when there are no joined participants)",
    ),
});

export type ActivityDistribution = z.infer<typeof ActivityDistributionSchema>;

export const JoiningToFirstRequestSchema = z.object({
  senders: z
    .number()
    .int()
    .min(0)
    .describe(
      "Joined participants with a first JOINED entry who sent at least one non-cancelled request",
    ),
  medianMs: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Nearest-rank median of first JOINED to first request sent, in ms (null when there are no senders)",
    ),
  within1h: z
    .number()
    .int()
    .min(0)
    .describe("Senders whose first request came at most 1 hour after joining"),
});

export type JoiningToFirstRequest = z.infer<typeof JoiningToFirstRequestSchema>;

export const ProfileFieldsFilledSchema = z.object({
  participants: z
    .number()
    .int()
    .min(0)
    .describe("Non-removed participants counted"),
  photo: z.number().int().min(0).describe("Participants with a photo"),
  name: z
    .number()
    .int()
    .min(0)
    .describe("Participants with a non-blank first, last or full name"),
  title: z
    .number()
    .int()
    .min(0)
    .describe("Participants with a non-blank title"),
  bio: z.number().int().min(0).describe("Participants with a non-blank bio"),
  linkedInSummary: z
    .number()
    .int()
    .min(0)
    .describe("Participants with a non-blank LinkedIn summary"),
  linkedInUrl: z
    .number()
    .int()
    .min(0)
    .describe("Participants with a personal LinkedIn URL"),
});

export type ProfileFieldsFilled = z.infer<typeof ProfileFieldsFilledSchema>;

export const ActivitySpreadSchema = z.object({
  joined: z
    .number()
    .int()
    .min(0)
    .describe("Currently JOINED participants: the population of the spread"),
  sent: ActivityDistributionSchema.describe(
    "Non-cancelled requests sent per joined participant",
  ),
  received: ActivityDistributionSchema.describe(
    "Non-cancelled requests received per joined participant",
  ),
  connections: ActivityDistributionSchema.describe(
    "Accepted requests, either direction, per joined participant",
  ),
  joinedNobodyRequested: z
    .number()
    .int()
    .min(0)
    .describe("Joined participants who received no non-cancelled request"),
  joiningToFirstRequest: JoiningToFirstRequestSchema.describe(
    "How quickly joined senders sent their first request after joining",
  ),
});

export type ActivitySpread = z.infer<typeof ActivitySpreadSchema>;

export const TopNetworkerSchema = z.object({
  participantIdPublic: EventParticipantIdPublicSchema.describe(
    "Public id of this networker",
  ),
  name: z
    .string()
    .describe("Resolved participant display name, 'Unknown' when nameless"),
  title: z
    .string()
    .nullable()
    .describe("Professional title (null when absent or blank)"),
  value: z
    .number()
    .int()
    .min(0)
    .describe("Metric value (sent or received count) ranking this networker"),
});

export type TopNetworker = z.infer<typeof TopNetworkerSchema>;

export const ParticipantRowSchema = z.object({
  participantIdPublic: EventParticipantIdPublicSchema.describe(
    "Public id of this participant",
  ),
  name: z
    .string()
    .describe("Resolved participant display name, 'Unknown' when nameless"),
  title: z
    .string()
    .nullable()
    .describe("Professional title (null when absent or blank)"),
  email: z.string().nullable().describe("Primary email (null when absent)"),
  status: EventParticipantStatusSchema.describe("Current participant status"),
  received: z
    .number()
    .int()
    .min(0)
    .describe("Requests received by this participant"),
  sent: z.number().int().min(0).describe("Requests sent by this participant"),
  accepted: z
    .number()
    .int()
    .min(0)
    .describe("Requests in accepted tier for this participant"),
  seen: z.number().int().min(0).describe("Requests in seen tier"),
  declined: z.number().int().min(0).describe("Requests in declined tier"),
  pending: z
    .number()
    .int()
    .min(0)
    .describe("Requests in pending/sent tier for this participant"),
});

export type ParticipantRow = z.infer<typeof ParticipantRowSchema>;

export const EventDashboardSchema = z.object({
  asOf: z.coerce
    .date()
    .describe("Instant this dashboard was computed, for right-censoring"),
  removedParticipants: z
    .number()
    .int()
    .min(0)
    .describe(
      "Participants in REMOVED status, excluded from every other block",
    ),
  funnel: z
    .array(FunnelStepSchema)
    .describe("Engagement funnel steps, ordered top to bottom"),
  vipFunnel: VipFunnelSchema.describe(
    "The funnel computed separately for VIP and other participants, percentages relative to each group",
  ),
  outcomes: RequestOutcomesSchema.describe(
    "Request outcomes over requests delivered to joined recipients",
  ),
  participantStatusOverTime: z
    .array(ParticipantTimeSeriesPointSchema)
    .describe(
      "Per-event-local-day participant status replay, gap-filled across the range",
    ),
  connectionsOverTime: z
    .array(ConnectionTimeSeriesPointSchema)
    .describe(
      "Per-event-local-day connection request counts by status, gap-filled across the range",
    ),
  joinCurve: JoinCurveSchema.describe(
    "When currently-JOINED participants first joined, by local day and event phase",
  ),
  requestPhases: RequestPhasesSchema.describe(
    "Non-cancelled requests and accepts split by the event window, each by its own timestamp",
  ),
  responseTime: ResponseTimeSchema.describe(
    "Time to open delivered requests, from max(created, recipient first JOINED) to first seen",
  ),
  requestNotes: RequestNotesSchema.describe(
    "How many requests carry a note (flag only, never the text), and accepts on delivered requests with vs without one",
  ),
  activitySpread: ActivitySpreadSchema.describe(
    "Per-person sent, received and connections spread over currently-JOINED participants",
  ),
  profileFieldsFilled: ProfileFieldsFilledSchema.describe(
    "How many non-removed participants have each profile field filled",
  ),
  topNetworkersBySent: z
    .array(TopNetworkerSchema)
    .describe("Top 20 participants by requests sent, descending"),
  topNetworkersByReceived: z
    .array(TopNetworkerSchema)
    .describe("Top 20 participants by requests received, descending"),
  participantRows: z
    .array(ParticipantRowSchema)
    .describe("One row per non-removed participant with connection rollups"),
});

export type EventDashboard = z.infer<typeof EventDashboardSchema>;
